<template>
  <v-container fluid class="pa-4">
    <div class="d-flex align-center mb-3">
      <v-icon class="mr-2">mdi-graph</v-icon>
      <h2 class="text-h5 mb-0">
        Сети ТГ каналов
        <HelpHint section="networks" text="Что такое сеть каналов и как читать граф" />
      </h2>
      <span v-if="computedAt" class="text-caption text-grey ml-4">пересчитано {{ formatDateTime(computedAt) }}</span>
      <v-spacer />
      <v-text-field
        v-model="search"
        density="compact"
        variant="outlined"
        hide-details
        clearable
        prepend-inner-icon="mdi-magnify"
        placeholder="Канал: название или @username"
        style="max-width: 360px;"
        @update:model-value="onSearch"
      />
    </div>

    <v-alert v-if="isAdmin && candidates.length" type="info" variant="tonal" density="compact" class="mb-3" icon="mdi-layers-search-outline">
      <div class="d-flex align-center flex-wrap ga-2">
        <span>Возможные агрегаторы (устойчиво репостят из {{ candidatesMeta.min_targets }}+ каналов за {{ candidatesMeta.window_days }} дн., репосты ≥ {{ Math.round(candidatesMeta.min_share * 100) }}% постов):</span>
        <v-chip v-for="c in candidates" :key="c.source_id" size="small" variant="outlined" color="deep-orange" closable close-icon="mdi-check" :title="`репостит из ${c.forward_targets} каналов (≥${candidatesMeta.min_weight} репостов за ${candidatesMeta.window_days} дн.), репостов ${Math.round(c.forward_share * 100)}% из ${c.posts} постов; в своей сети сейчас ${c.network_size ?? '—'} каналов — нажмите ✓, чтобы пометить агрегатором`" @click:close="confirmAggregator(c)">
          {{ c.title || ('@' + c.username) }} · репостит из {{ c.forward_targets }} · {{ Math.round(c.forward_share * 100) }}% репостов<span v-if="c.network_size"> · сеть {{ c.network_size }}</span>
        </v-chip>
      </div>
    </v-alert>

    <div class="networks-layout d-flex">
      <!-- Список сеток -->
      <div class="list-column" :style="{ width: listWidth + 'px', minWidth: '220px' }">
        <v-card class="fill-height d-flex flex-column">
          <v-list density="compact" nav class="flex-grow-1 overflow-y-auto" :style="{ maxHeight: 'calc(100vh - 226px)' }">
            <v-list-item
              v-for="n in items"
              :key="n.id"
              :active="n.id === selectedId"
              color="primary"
              @click="select(n.id)"
            >
              <template #prepend>
                <v-avatar size="32" class="mr-2">
                  <v-img v-if="n.hub_thumbnail_url" :src="n.hub_thumbnail_url" cover />
                  <v-icon v-else size="small">mdi-account-group</v-icon>
                </v-avatar>
              </template>
              <v-list-item-title class="text-body-2">{{ n.hub_title || n.name }}</v-list-item-title>
              <v-list-item-subtitle class="text-caption">
                {{ n.size }} каналов в сети
                <span v-if="n.matched_members && n.matched_members.length"> · найдено: {{ n.matched_members.map(m => m.title || ('@' + m.username)).join(', ') }}</span>
              </v-list-item-subtitle>
            </v-list-item>
            <div v-if="!loading && !items.length" class="text-center text-caption text-grey pa-6">
              Сетки ещё не рассчитаны или ничего не найдено
            </div>
          </v-list>
          <v-divider />
          <div class="d-flex align-center justify-space-between px-3 py-2 text-caption text-grey">
            <span>Всего: {{ total }}</span>
            <v-pagination v-model="page" :length="pages" density="compact" total-visible="3" size="small" @update:model-value="load" />
          </div>
        </v-card>
      </div>

      <div class="splitter" title="Потяните, чтобы изменить ширину списка" @mousedown.prevent="startResize"></div>

      <!-- Граф выбранной сетки -->
      <div class="graph-column flex-grow-1">
        <v-card class="fill-height">
          <v-alert
            v-if="retiredInfo"
            type="warning"
            variant="tonal"
            density="compact"
            class="ma-2"
            icon="mdi-history"
          >
            Эта сеть из пересчёта {{ formatDateTime(retiredInfo.retired_at) }} и в актуальном списке отсутствует: её состав изменился при
            следующем пересчёте. Показан состав на тот момент.
            <v-btn v-if="retiredInfo.successor_id" size="small" variant="text" color="warning" class="ml-2" @click="select(retiredInfo.successor_id)">
              Перейти к актуальной: {{ retiredInfo.successor_name || ('#' + retiredInfo.successor_id) }}
            </v-btn>
          </v-alert>
          <ChannelGraph
            v-if="selectedId"
            :key="selectedId"
            :network-id="selectedId"
            :highlight-username="highlightUsername"
            show-header
            height="calc(100vh - 246px)"
            @recenter="onRecenter"
          />
          <div v-else class="d-flex align-center justify-center text-grey" style="height: calc(100vh - 246px);">
            Выберите сетку слева или найдите канал
          </div>
        </v-card>
      </div>
    </div>

    <ChannelGraphModal v-model="showEgo" :source-id="ego.sourceId" :username="ego.username" :title="ego.title" />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import apiClient from '@/apiClient';
import ChannelGraph from '@/components/ChannelGraph.vue';
import ChannelGraphModal from '@/components/ChannelGraphModal.vue';

export default {
  name: 'Networks',
  components: { ChannelGraph, ChannelGraphModal },
  data() {
    return {
      items: [],
      total: 0,
      page: 1,
      pageSize: 50,
      computedAt: null,
      loading: false,
      search: '',
      searchTimer: null,
      selectedId: null,
      retiredInfo: null,   // выбранная сеть выведена из актуальных (хранится 7 дней) → баннер + преемник
      showEgo: false,
      ego: { sourceId: null, username: '' },
      listWidth: 320,
      candidates: [],
      candidatesMeta: { min_targets: 300, min_share: 0.4, window_days: 180, min_weight: 2 },
    };
  },
  computed: {
    ...mapGetters(['isAdmin']),
    pages() { return Math.max(1, Math.ceil(this.total / this.pageSize)); },
    // при поиске по каналу — выделить его на графе выбранной сети
    highlightUsername() {
      if (!this.search) return '';
      const n = this.items.find(i => i.id === this.selectedId);
      const m = n && n.matched_members && n.matched_members[0];
      return m ? (m.username || '') : '';
    },
  },
  watch: {
    '$route.query.network'(v) { if (v && Number(v) !== this.selectedId) this.selectedId = Number(v); },
  },
  beforeUnmount() { this.stopResize(); },
  mounted() {
    try {
      const raw = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('tg_networks_layout='));
      const saved = raw ? JSON.parse(decodeURIComponent(raw.slice('tg_networks_layout='.length))) : null;
      if (saved && Number.isFinite(saved.listWidth)) this.listWidth = Math.min(700, Math.max(220, saved.listWidth));
      // последняя выбранная сеть — если в адресе не задана другая
      if (saved && Number.isFinite(saved.selectedId) && !this.$route.query.network) this.selectedId = saved.selectedId;
    } catch (e) { /* без cookie */ }
    const q = this.$route.query;
    if (q.q) this.search = q.q;
    if (q.network) this.selectedId = Number(q.network);
    this.load();
    if (this.isAdmin) this.loadCandidates();
  },
  methods: {
    async loadCandidates() {
      try {
        const { data } = await apiClient.get('/tg-networks/aggregator-candidates');
        this.candidates = data.items || [];
        this.candidatesMeta = { min_targets: data.min_targets, min_share: data.min_share, window_days: data.window_days, min_weight: data.min_weight };
      } catch (e) { this.candidates = []; }
    },
    async confirmAggregator(c) {
      try {
        await apiClient.put(`/sources/${c.source_id}/aggregator`, { is_aggregator: true });
        this.candidates = this.candidates.filter(x => x.source_id !== c.source_id);
      } catch (e) { console.error('aggregator flag error', e); }
    },
    saveLayout() {
      try { document.cookie = `tg_networks_layout=${encodeURIComponent(JSON.stringify({ listWidth: this.listWidth, selectedId: this.selectedId }))};path=/;max-age=31536000`; } catch (e) { /* без cookie */ }
    },
    startResize(e) {
      this._resizeStartX = e.clientX; this._resizeStartW = this.listWidth;
      this._onMove = (ev) => { this.listWidth = Math.min(700, Math.max(220, this._resizeStartW + (ev.clientX - this._resizeStartX))); };
      this._onUp = () => { this.stopResize(); this.saveLayout(); };
      window.addEventListener('mousemove', this._onMove); window.addEventListener('mouseup', this._onUp);
      document.body.style.userSelect = 'none';
    },
    stopResize() {
      if (this._onMove) window.removeEventListener('mousemove', this._onMove);
      if (this._onUp) window.removeEventListener('mouseup', this._onUp);
      this._onMove = null; this._onUp = null; document.body.style.userSelect = '';
    },
    formatDateTime(d) { return d ? new Date(d).toLocaleString('ru-RU') : ''; },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.page = 1; this.load(); }, 300);
    },
    async load() {
      this.loading = true;
      try {
        const params = { page: this.page, page_size: this.pageSize };
        if (this.search) params.search = this.search;
        const { data } = await apiClient.get('/tg-networks', { params });
        this.items = data.items || [];
        this.total = data.total || 0;
        this.computedAt = data.computed_at;
        // Поиск по каналу: если нашлась ровно одна сетка — открываем её сразу
        if (this.search && this.items.length === 1) this.select(this.items[0].id);
        else if (!this.selectedId && this.items.length && !this.search) this.select(this.items[0].id);
        else if (this.selectedId && !this.search && !this.items.some(n => n.id === this.selectedId)) this.checkRetired(this.selectedId);
        if (this.selectedId && this.items.some(n => n.id === this.selectedId)) this.retiredInfo = null;
      } catch (e) {
        console.error('networks load error', e);
      } finally { this.loading = false; }
    },
    async checkRetired(id) {
      // Сеть не в актуальном списке: либо выведена (открываем со снимком и баннером), либо удалена совсем
      try {
        const { data } = await apiClient.get(`/tg-networks/${id}`);
        this.retiredInfo = data.is_current ? null : data;
      } catch (e) {
        this.retiredInfo = null;
        if (this.items.length) this.select(this.items[0].id);
      }
    },
    select(id) {
      this.retiredInfo = null;
      this.selectedId = id;
      this.$router.replace({ query: { ...this.$route.query, network: id } }).catch(() => {});
      this.saveLayout();
    },
    onRecenter(node) {
      // Из сетки — в эго-граф канала (соседи вне сетки тоже видны)
      this.ego = { sourceId: node.source_id, username: node.username, title: node.title || '' };
      this.showEgo = true;
    },
  },
};
</script>

<style scoped>
.networks-layout { min-height: calc(100vh - 206px); }
.list-column { max-height: calc(100vh - 206px); flex: 0 0 auto; }
.graph-column { min-width: 0; padding-left: 4px; }
.splitter { flex: 0 0 6px; cursor: col-resize; background: rgba(0,0,0,0.04); border-radius: 3px; }
.splitter:hover { background: rgba(25,118,210,0.25); }
</style>
